/* import __COLOCATED_TEMPLATE__ from './steps.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import safeWindowOpen from 'embercom/lib/safe-window-open';
import type SalesforceConfiguration from 'embercom/models/standalone/salesforce-configuration';
import { type SalesforceSetupData } from 'embercom/objects/standalone/salesforce';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import type IntlService from 'embercom/services/intl';
// @ts-ignore
import { icInterfaceIcon } from '@intercom/pulse/helpers/ic-interface-icon';
import { isEmpty } from '@ember/utils';
import type AppService from 'embercom/services/app-service';

const FULL_PERMISSION_SET_PACKAGE_ID = '04tgK00000031PN';
const CASES_PERMISSION_SET_PACKAGE_ID = '04tgK0000005DXh';
const PERMISSIONS_UNLOCKED_PACKAGE_ID = '04tgK0000006i9dQAA';

interface Args {
  hideAssignStep?: boolean;
}

export default class PermissionsSteps extends Component<Args> {
  @service declare appService: AppService;
  @service declare finStandaloneService: FinStandaloneService;
  @service declare intl: IntlService;

  get salesforcePlatform() {
    return this.finStandaloneService.salesforcePlatform;
  }

  get salesforceConfig(): SalesforceConfiguration {
    return this.salesforcePlatform.salesforceConfig!;
  }

  get salesforceSetupData(): SalesforceSetupData {
    return this.salesforcePlatform.salesforceSetupData;
  }

  get isDisabled() {
    return this.isLoading || this.isComplete || this.salesforceConfig?.isPendingSetup || false;
  }

  get isComplete() {
    return this.salesforceSetupData.hasPermissionSet;
  }

  get isLoading() {
    return (
      this.salesforcePlatform.salesforceDataLoader.isLoadingSalesforceSetupData ||
      (this.salesforcePlatform.salesforceDataLoader.isLoadingSalesforcePermissions &&
        !this.salesforceSetupData.hasPermissionSet)
    );
  }

  get shouldShowCasesOnlyPermissions() {
    return !this.isDisabled && !this.appService.app.canInstallSalesforceUnlockedPackage;
  }

  get installationSteps() {
    let prefix = 'standalone.salesforce.setup.permissions.steps.install-section';

    return [
      { label: this.intl.t(`${prefix}.step-1`) },
      { label: this.intl.t(`${prefix}.step-2`, { htmlSafe: true }) },
      { label: this.intl.t(`${prefix}.step-3`, { htmlSafe: true }) },
    ];
  }

  get configureAndAssignSteps() {
    let prefix = 'standalone.salesforce.setup.permissions.steps.configure-and-assign-section';
    let step2Key = this.appService.app.canInstallSalesforceUnlockedPackage
      ? 'step-2'
      : 'step-2-legacy';
    let step3Key = this.appService.app.canInstallSalesforceUnlockedPackage
      ? 'step-3'
      : 'step-3-legacy';

    return [
      {
        label: this.intl.t(`${prefix}.step-1`, {
          htmlSafe: true,
          openUrl: this.userInterfaceSettingsUrl,
          icon: icInterfaceIcon(['new-window'], {}),
        }),
      },
      {
        label: this.intl.t(`${prefix}.${step2Key}`, {
          htmlSafe: true,
          openUrl: this.permissionSetsUrl,
          icon: icInterfaceIcon(['new-window'], {}),
        }),
      },
      {
        label: this.intl.t(`${prefix}.${step3Key}`, {
          htmlSafe: true,
          installerName: this.salesforceUserInstallerName,
          openUrl: this.permissionSetAssignmentsUrl,
          icon: icInterfaceIcon(['new-window'], {}),
          personIcon: icInterfaceIcon(['person'], {}),
        }),
      },
    ];
  }

  get salesforceUserInstallerName() {
    if (isEmpty(this.salesforceConfig.userId)) {
      return undefined;
    }

    let user = this.salesforcePlatform.salesforceSetupData.admins.find(
      (admin) => admin.id === this.salesforceConfig.userId,
    );

    if (user && user.name) {
      return user.name;
    }

    return this.salesforceConfig.userId;
  }

  @action
  openFullPermissionSetUrl() {
    safeWindowOpen(this.fullPermissionSetUrl, '_blank');
  }

  get fullPermissionSetUrl() {
    if (this.appService.app.canInstallSalesforceUnlockedPackage) {
      return this.buildPermissionSetUrl(PERMISSIONS_UNLOCKED_PACKAGE_ID);
    }

    return this.buildPermissionSetUrl(FULL_PERMISSION_SET_PACKAGE_ID);
  }

  get casesOnlyPermissionSetUrl() {
    return this.buildPermissionSetUrl(CASES_PERMISSION_SET_PACKAGE_ID);
  }

  buildPermissionSetUrl(permissionSetId: string) {
    let loginSubdomain = this.salesforceConfig.isSandbox ? 'test' : 'login';
    return `https://${loginSubdomain}.salesforce.com/packaging/installPackage.apexp?p0=${permissionSetId}`;
  }

  get userInterfaceSettingsUrl() {
    return this.salesforceConfig.buildLightningSetupUrl('UserInterfaceUI/home');
  }

  get permissionSetsUrl() {
    return this.salesforceConfig.buildLightningSetupUrl('PermSets/home');
  }

  get permissionSetAssignmentsUrl() {
    return this.salesforceConfig.buildLightningSetupUrl(
      `PermSets/page?address=/udd/PermissionSet/assignPermissionSet.apexp?userId=${this.salesforceConfig.userId}`,
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Salesforce::Setup::Permissions::Steps': typeof PermissionsSteps;
  }
}
